import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from 'react-apollo'
import { NavLink } from 'react-router-dom'
import Imgix from '../imgix'
import styles from './confirmation-view.css'
import checkIcon from '../../../images/forms-check.svg'

import { STATIC_SCREENS } from '../../constants'

import { E2E_SELECTORS } from '../../e2e-selectors'
import Button from '../button'
import { LoadingModal } from '../loading/modal'
import RENTAL_CONFIRMATION_VIEW_QUERY from '../../../graphql/queries/rental-confirmation-view.gql'
import CONTENT_ITEM_QUERY from '../../../graphql/queries/content-item.gql'
import MY_RENTALS_QUERY from '../../../graphql/queries/my-rentals.gql'
import { segmentTrackCompletePurchase } from '../../segment/segment-track'
import { decodeHTMLEntity } from '../../lib/utils'

function ConfirmationView({ orderId, contentItemId, isAuthenticated }) {
  // fire query rentalConfirmationView for displaying the info on the confirmation view
  const {
    data: rentalConfirmationData,
    loading: rentalConfirmationLoading,
    error: rentalConfirmationError
  } = useQuery(RENTAL_CONFIRMATION_VIEW_QUERY, {
    variables: {
      orderId,
      contentItemId
    },
    fetchPolicy: 'network-only'
  })

  // fire query contentItem for sending segment event
  const {
    data: contentItemQueryData,
    loading: contentItemQueryLoading,
    error: contentItemQueryError
  } = useQuery(CONTENT_ITEM_QUERY, {
    variables: {
      id: contentItemId
    },
    fetchPolicy: 'cache-first'
  })

  const shouldFetchMyRentals = Boolean(
    rentalConfirmationData?.rentalConfirmationView
  )

  // fire query myRentalHistory after query rentalConfirmationView is successful, to get the latest rental history in cache
  const {
    loading: myRentalsQueryLoading,
    error: myRentalsQueryError
  } = useQuery(MY_RENTALS_QUERY, {
    fetchPolicy: 'network-only',
    skip: !shouldFetchMyRentals
  })

  const hasTrackedPurchaseRef = useRef(false)

  // send segment event only once
  useEffect(() => {
    if (hasTrackedPurchaseRef.current) return
    const contentItem = contentItemQueryData?.contentItem
    if (!contentItem) return

    // eslint-disable-next-line object-curly-newline
    const { title, isRental, ldId, products } = contentItem
    segmentTrackCompletePurchase({
      title,
      isRental,
      ldId,
      payment: Number(products[0]?.currentPrice)
    })
    hasTrackedPurchaseRef.current = true
  }, [contentItemQueryData])

  const isLoadingOrError =
    rentalConfirmationLoading ||
    rentalConfirmationError ||
    contentItemQueryLoading ||
    contentItemQueryError ||
    myRentalsQueryLoading ||
    myRentalsQueryError

  if (isLoadingOrError || !rentalConfirmationData) {
    return <LoadingModal />
  }

  const rentalConfirmationView =
    rentalConfirmationData.rentalConfirmationView || {}
  const header = rentalConfirmationView.header || ''
  const image = rentalConfirmationView.image?.uri || ''
  const notes = rentalConfirmationView.notes || []
  const details = rentalConfirmationView.details || []
  const cta = rentalConfirmationView.cta || ''

  return (
    <div className={styles.wrapper}>
      <div className={styles.imageWrapper}>
        <Imgix
          src={image}
          recipe="purchaseConfirmation"
          className={styles.image}
        />
      </div>
      <div className={styles.confirmationWrapper}>
        <div className={styles.confirmationHeader}>
          <div className={styles.productInfo}>
            <div className={styles.title}>{header}</div>
            <div className={styles.subheader}>
              Your movie is now available on any device. To watch, go to
              {' '}
              <NavLink to={STATIC_SCREENS.MY_RENTALS.screenId}>
                My Rentals
              </NavLink>
            </div>
            {/** notes (bullet points) */}
            <div className={styles.purchaseInfo}>
              {notes.map((note, index) => (
                <span
                  key={index} // eslint-disable-line react/no-array-index-key
                >
                  {note}
                </span>
              ))}
            </div>
            {/** <cardNickname> has been sucessfully charged <chargedPrice> */}
            <div className={styles.details}>
              {details.map(detail => {
                const detailName = decodeHTMLEntity(detail.name || '')
                return (
                  <div key={detail.name}>
                    <div className={styles.detailLabel}>{detailName}</div>
                    {detail.value && (
                      <div className={styles.detail}>
                        {detail.icon && (
                          <img
                            className={styles.detailImage}
                            alt={detailName}
                            src={detail.icon}
                          />
                        )}
                        <div className={styles.detailValue}>
                          {detail.type === 'cc'
                            ? `···· ···· ···· ${detail.value.substr(-4)}`
                            : detail.value}
                        </div>
                        <img
                          alt="valid"
                          className={styles.detailCheck}
                          src={checkIcon}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <NavLink
              to={
                isAuthenticated
                  ? STATIC_SCREENS.MY_RENTALS.screenId
                  : '/?modal=login'
              }
            >
              <Button
                className={styles.gotoMyRentals}
                text={cta}
                e2eIdentifier={E2E_SELECTORS.GO_TO_MY_RENTALS_BUTTON}
              />
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  )
}

ConfirmationView.propTypes = {
  orderId: PropTypes.string.isRequired,
  contentItemId: PropTypes.string.isRequired,
  isAuthenticated: PropTypes.bool.isRequired
}

export default ConfirmationView
